import React, { ChangeEvent, FocusEvent, Ref } from 'react';
import classNames from 'classnames';

import styles from './InputBox.module.scss';

interface Props {
  centerLabel?: boolean;
  customMargin?: string | number;
  customWidth?: string | number;
  error?: string;
  hasErrorStyle?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  isLong?: boolean;
  isProProfile?: boolean;
  isRequired?: boolean;
  label?: string;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  pattern?: string;
  placeholder?: string;
  rows?: number;
  subtitle?: string;
  type?: string;
  value?: string;
}

/**
 * The class ".input-box" is use on a wrapper for the input or textarea, this way yo u can have
 * a label, subtitle or error message everything is handled in the same component
 * Simply use the "<InputBox />" component with the necessary props
 */
const InputBox = React.forwardRef<HTMLElement, Props>(
  (
    {
      centerLabel = false,
      customMargin = '16px 0',
      customWidth,
      hasErrorStyle = false,
      inputMode,
      isLong = false,
      isProProfile = false,
      isRequired = false,
      error,
      label,
      name,
      onBlur,
      onChange,
      pattern,
      placeholder = '',
      rows = 3,
      subtitle = '',
      type = 'text',
      value,
    },
    ref: Ref<HTMLElement>
  ) => (
    <div
      className={classNames({
        [styles['input-box']]: true,
        [styles['input-invalid']]: hasErrorStyle || error,
        [styles['input-long']]: isLong,
      })}
      style={{ margin: customMargin, width: customWidth }}
    >
      {label && (
        <label htmlFor={name} className={classNames({ [styles.center]: centerLabel })}>
          {label}
        </label>
      )}
      {subtitle && <p className="input-subtitle">{subtitle}</p>}
      {type === 'textarea' ? (
        <textarea
          ref={ref as Ref<HTMLTextAreaElement>}
          className={classNames({
            [styles['input-pro-profile-field']]: isProProfile,
          })}
          id={name}
          name={name}
          placeholder={placeholder}
          value={value}
          rows={rows}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={!onChange}
          required={isRequired}
        />
      ) : (
        <input
          ref={ref as Ref<HTMLInputElement>}
          className={classNames({
            [styles['input-pro-profile-field']]: isProProfile,
          })}
          id={name}
          type={type}
          name={name}
          inputMode={inputMode}
          pattern={pattern}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={!onChange}
          required={isRequired}
        />
      )}
      {error && <p className="input-error">{error}</p>}
    </div>
  )
);
InputBox.displayName = 'InputBox';

export default InputBox;
export type { Props as InputBoxProps };
